import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Button, Paper, Typography } from '@mui/material';
import { formatDateTimePH, formatMessageDates } from '../utils/dateFormat';

export default function InboxPanel({ onUnreadCountChange }) {
  const API_BASE = process.env.REACT_APP_API_BASE || '';
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState('');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/api/notifications`, { withCredentials: true });
      const rows = res.data || [];
      setNotifications(rows);
      if (typeof onUnreadCountChange === 'function') {
        const unread = Array.isArray(rows) ? rows.filter((n) => !n.is_read).length : 0;
        onUnreadCountChange(unread);
      }
    } catch (err) {
      console.error('Failed to load notifications', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, onUnreadCountChange]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadCount = Array.isArray(notifications) ? notifications.filter((n) => !n.is_read).length : 0;

  const markAllRead = async () => {
    if (!unreadCount || markingAll) return;
    setMarkingAll(true);
    try {
      await axios.put(`${API_BASE}/api/notifications/read-all`, {}, { withCredentials: true });
      await fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all notifications read', err);
      setError('Failed to mark all notifications read');
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Inbox</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>Your notifications</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={fetchNotifications}>Refresh</Button>
        <Button variant="contained" onClick={markAllRead} disabled={!unreadCount || markingAll}>
          {markingAll ? 'Marking...' : 'Mark All Read'}
        </Button>
      </Box>
      {loading && <Typography>Loading...</Typography>}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && !error && Array.isArray(notifications) && notifications.length === 0 && (
        <Typography color="text.secondary">No notifications</Typography>
      )}
      {!loading && Array.isArray(notifications) && notifications.map((n) => (
        <Paper key={n.id} sx={{ p: 2, mb: 1, bgcolor: n.is_read ? 'transparent' : 'rgba(59,130,246,0.06)' }}>
          <Typography variant="body1">{formatMessageDates(n.message)}</Typography>
          <Typography variant="caption" color="text.secondary">{formatDateTimePH(n.created_at)}</Typography>
          {!n.is_read && (
            <Button
              size="small"
              sx={{ ml: 2 }}
              onClick={async () => {
                try {
                  await axios.put(`${API_BASE}/api/notifications/${n.id}/read`, {}, { withCredentials: true });
                  await fetchNotifications();
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              Mark read
            </Button>
          )}
        </Paper>
      ))}
    </Box>
  );
}
